import { inject, Injectable } from "@angular/core";
import { EnvironmentService } from "../environment.service";
import { AppStateService } from "../../store/state/app/app.state.service";
import { OrderStateServiceWrapper } from "../../store/state/order/order.state.wrapper.service";
import { DataService } from "../data.service";
import { HttpHeaders } from "@angular/common/http";
import { WirelessValidateBYODDetailsRequest } from "../../models/wireless.model";
import { SingleLineConfiguration } from "../../models/line-configurations.model";
import { ViLineData } from "../../models/sigfan.model";
import { OrderStateServiceV2 } from "../../store/state/order/orderv2/order-state.service";
import { OrderSetupStateService } from "../../store/state/order/orderv2/workflows/order-setup/order-setup-state.service";
import { InternetPackageSelectionStateService } from "../../store/state/order/orderv2/workflows/internet/internet-package-selection/internet-package-selection-state.service";
import { DeviceShippingOption } from "../../models/ShippingOption";
import { Observable } from "rxjs";
import { WirelessSecureAccountStore } from "../../../features/orders/wireless-secure-account.store";
import { OrderApiCacheService } from "../../store/state/order/orderv2/order-api-response/order-api-cache.service";
import { BusinessVerificationStateService } from "../../store/state/order/orderv2/workflows/wireless/business-verification/business-verification.state.service";
import { WirelessAdditionalOffersStateService } from "../../store/state/order/orderv2/workflows/wireless/wireless-additional-offers.state.service";
import { WirelessFlowStateService } from "../../store/state/order/orderv2/workflows/wireless/wireless-flow-state.service";
import { WirelessLineConfigurationStateService } from "../../store/state/order/orderv2/workflows/wireless/wireless-line-configuration.state.service";
import { WirelessSetupStateService } from "../../store/state/order/orderv2/workflows/wireless/wireless-setup.state.service";
import { WirelessSigfanStateService } from "../../store/state/order/orderv2/workflows/wireless/wireless-sigfan.state.service";

@Injectable({
  providedIn: 'root'
})
export class WirelessOrderService {

  #environmentService = inject(EnvironmentService)
  #appStateService = inject(AppStateService)
  #orderStateService = inject(OrderStateServiceV2)
  #orderStateServiceOld = inject(OrderStateServiceWrapper)
  orderSetupStateService = inject(OrderSetupStateService);
  #dataService = inject(DataService)
  #internetPackageSelectionStateService = inject(InternetPackageSelectionStateService);
  private orderApiCacheService = inject(OrderApiCacheService);
  private orderStateService = inject(OrderStateServiceWrapper);

  private wirelessSetupStateService = inject(WirelessSetupStateService);
  private wirelessAdditionalOffersStateService = inject(WirelessAdditionalOffersStateService);
  private wirelessLineConfigurationStateService = inject(WirelessLineConfigurationStateService);
  private sigfanStateService = inject(WirelessSigfanStateService);
  private wirelessFlowStateService = inject(WirelessFlowStateService);
  private businessVerificationStateService = inject(BusinessVerificationStateService);
  private secureAccountStore = inject(WirelessSecureAccountStore);
  #reservePhoneNumberUrl = this.#environmentService.getUrl('ng/v1/vi/reservenumber/')
  #vivalidatebyodUrl = this.#environmentService.getUrl('ng/v1/vi/validatebyod/')
  #viverifyportUrl = this.#environmentService.getUrl('ng/v1/vi/verifyport/')
  #viaddportUrl = this.#environmentService.getUrl('ng/v1/vi/addport/')
  #viUpgradeQuestionsUrl = this.#environmentService.getUrl('ng/v1/vi/upgradequestions/')
  #viCostcoValidateMemberUrl = this.#environmentService.getUrl('ng/v1/vi/costcovalidatemember/')
  #viTradeInStartUrl = this.#environmentService.getUrl('ng/v1/vi/tradeinstart/')
  #viTradeInQuestionsUrl = this.#environmentService.getUrl('ng/v1/vi/tradeinquestions/')
  #viTradeInSubmitUrl = this.#environmentService.getUrl('ng/v1/vi/tradeinsubmit/')

  get selectedDealerCode() {
    return this.orderSetupStateService.getSelectedDealerCode();
  }

  get selectedCustomer() {
    return this.#internetPackageSelectionStateService.serviceAvailabilityData()?.Customer;
  }


  public createNewPhoneNumber(lineNumber) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.selectedDealerCode;

    const customer = this.selectedCustomer;
    const endpoint = this.#reservePhoneNumberUrl + sessionId + '/line/' + lineNumber;

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: customer?.Email,
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress.Address1,
        Address2: customer?.ServiceAddress.Address2,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
        CountryCode: customer?.ServiceAddress.CountryCode
      },
      CustomizationInfo: {
        Category: "Wireless",
        Details: {
          Number: lineNumber,
          TransactionNumber: ''
        }
      }
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  public validateDeviceIMEI(details: WirelessValidateBYODDetailsRequest, lineNumber) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.selectedDealerCode;

    const customer = this.selectedCustomer;
    const endpoint = this.#vivalidatebyodUrl + sessionId + '/line/' + lineNumber;

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: customer?.Email,
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress.Address1,
        Address2: customer?.ServiceAddress.Address2,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
        CountryCode: customer?.ServiceAddress.CountryCode
      },
      CustomizationInfo: {
        Category: "Wireless",
        Details: details
      }
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  public verifyPortNumber(lineNumber, TransactionNumber) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.selectedDealerCode;

    const customer = this.selectedCustomer;
    const endpoint = this.#viverifyportUrl + sessionId + '/line/' + lineNumber;

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: customer?.Email,
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress.Address1,
        Address2: customer?.ServiceAddress.Address2,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
        CountryCode: customer?.ServiceAddress.CountryCode
      },
      CustomizationInfo: {
        Category: "Wireless",
        Details: {
          Number: lineNumber,
          TransactionNumber: TransactionNumber
        }
      }
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  public portNumber(Details, lineNumber) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.selectedDealerCode;

    const customer = this.selectedCustomer;
    const endpoint = this.#viaddportUrl + sessionId + '/line/' + lineNumber;

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: customer?.Email,
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress.Address1,
        Address2: customer?.ServiceAddress.Address2,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
        CountryCode: customer?.ServiceAddress.CountryCode
      },
      CustomizationInfo: {
        Category: "Wireless",
        Details
      }
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  viCostcoValidateMember(MemberId: string) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.selectedDealerCode;

    const customer = this.selectedCustomer;
    const endpoint = this.#viCostcoValidateMemberUrl + sessionId;

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: customer?.Email,
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress.Address1,
        Address2: customer?.ServiceAddress.Address2,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
        CountryCode: customer?.ServiceAddress.CountryCode
      },
      CustomizationInfo: {
        Category: "Wireless",
        MemberId
      }
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  viTradeInStart(lineNumber: number) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.selectedDealerCode;

    const allLines = this.getAllLinesRequestPayload();
    const currentLine = allLines.find(line => line.Number === lineNumber);

    const customer = this.selectedCustomer;
    const endpoint = this.#viTradeInStartUrl + sessionId;

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: customer?.Email,
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress.Address1,
        Address2: customer?.ServiceAddress.Address2,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
        CountryCode: customer?.ServiceAddress.CountryCode
      },
      CustomizationInfo: {
        Category: "Wireless",
        Details: {
          TransactionNumber: currentLine?.TransactionNumber ?? '',
          TransactionType: currentLine?.TransactionType,
        }
      }
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  viTradeInQuestions(tradeInModelCode: string, tradeInDeviceImei: string, lineNumber: number) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.selectedDealerCode;

    const allLines = this.getAllLinesRequestPayload();
    const currentLine = allLines.find(line => line.Number === lineNumber);

    const customer = this.selectedCustomer;
    const endpoint = this.#viTradeInQuestionsUrl + sessionId + '?number=' + lineNumber;

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: customer?.Email,
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress.Address1,
        Address2: customer?.ServiceAddress.Address2,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
        CountryCode: customer?.ServiceAddress.CountryCode
      },
      CustomizationInfo: {
        Category: "Wireless",
        Details: {
          TransactionNumber: currentLine?.TransactionNumber ?? '',
          TransactionType: currentLine?.TransactionType,
          TradeInDeviceImei: tradeInDeviceImei,
          TradeInModelCode: tradeInModelCode
        }
      }
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  viTradeInSubmit(tradeInModelCode: string, tradeInDeviceImei: string, lineNumber: number, deviceCondition: { [key: string]: string }) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.selectedDealerCode;

    const allLines = this.getAllLinesRequestPayload();
    const currentLine = allLines.find(line => line.Number === lineNumber);
    const deviceSku = currentLine?.DeviceDetails?.DeviceSku ?? ''

    const customer = this.selectedCustomer;
    const endpoint = this.#viTradeInSubmitUrl + sessionId;

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: customer?.Email,
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress.Address1,
        Address2: customer?.ServiceAddress.Address2,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
        CountryCode: customer?.ServiceAddress.CountryCode
      },
      CustomizationInfo: {
        Category: "Wireless",
        Details: {
          TransactionNumber: currentLine?.TransactionNumber ?? '',
          TransactionType: currentLine?.TransactionType,
          NewDeviceSku: deviceSku,
          TradeInDeviceImei: tradeInDeviceImei,
          TradeInModelCode: tradeInModelCode,
          DeviceCondition: deviceCondition
        }
      }
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  public upgradequestions(DeviceAnswers, lineNumber, existingLineInfo: ViLineData) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.selectedDealerCode;

    const customer = this.selectedCustomer;
    const endpoint = this.#viUpgradeQuestionsUrl + sessionId + '/line/' + lineNumber;
    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: customer?.Email,
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress.Address1,
        Address2: customer?.ServiceAddress.Address2,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
        CountryCode: customer?.ServiceAddress.CountryCode
      },
      CustomizationInfo: {
        Category: "Wireless",
        LineNumber: lineNumber,
        DeviceImei: existingLineInfo?.Device?.DeviceIMEI || '',
        TransactionNumber: existingLineInfo?.Tn || '',
        DeviceAnswers: DeviceAnswers
      }
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  private getSessionId(): string {
    return this.#appStateService.getSession()?.SessionID;
  }

  private getStandardHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json');
  }

  private getOrderBasicInfo() {
    const { OrderNumber, ConnId } = this.orderSetupStateService.preOrderData()
    const selectedDealerCode = this.selectedDealerCode;
    const OrderType = this.orderSetupStateService.getOrderType() as string;
    return { OrderNumber, ConnId, selectedDealerCode, OrderType };
  }

  getAllLinesRequestPayload() {
    const lines = [];
    const linesInProgress = this.#orderStateService.order()?.wirelessFlow?.numberOfWirelessLinesInProgress || 0
    for (let lineNumber = 1; lineNumber <= linesInProgress; lineNumber++) {
      lines.push(this.getLineDataFromState(lineNumber, true));
    }
    return lines;
  }

  getLineDataFromState(lineNumber: number, includePortInfo = false, active = true) {
    const customer = this.selectedCustomer;
    const lineConfigurationState = this.#orderStateService.order()?.wirelessFlow?.lineConfigurations;
    const existingLinesCount = this.getExistingLinesCount();

    let lineData: SingleLineConfiguration | null = null;
    if (lineConfigurationState) {
      lineData = lineConfigurationState[`line${lineNumber}`];
    }
    const deviceSku = lineData?.device?.selectedDevice?.ChoiceCode ?? ''
    const promotions = [];
    const wirelessPdPlusLine = this.wirelessLineConfigurationStateService.getLatestWirelessLine(lineNumber);

    if (lineData?.promotions && lineData.promotions.length > 0) {
      lineData.promotions.forEach(promo => {
        if (promo.type === 'standard') {
          promotions.push({
            Code: promo.promotionCode
          });
        } else if (promo.type === 'premiumTradeIn') {
          promotions.push({
            Code: promo.isAccepted ? promo.acceptChoiceCode : promo.declineChoiceCode,
            TradeInModel: promo.useAdvancedFlow ? promo?.deviceDetails?.TradeInModel : promo?.tradeInDevice,
            TradeInValue: promo.useAdvancedFlow ? (promo?.deviceDetails?.PromotionTradeInValue ?? promo?.deviceDetails?.TradeInValue) : promo?.tradeInValue?.trim(),
          });
        } else if (promo.type === 'ndsChoice') {
          promotions.push({
            Code: promo.isAccepted ? promo.acceptChoiceCode : promo.declineChoiceCode
          });
        }
      });
    }

    let byodSim = lineData?.device?.deviceSIM;
    if (!byodSim || byodSim.trim().length === 0) {
      byodSim = lineData?.device?.simIccid;
    }

    const shippingOption = lineData?.device?.shippingOption;
    const selectedStore = lineData?.device?.selectedStore;
    const selectedService = lineData?.device?.selectedLevelOfService;

    let shipTypeToken = '';
    let shipTypeId = '';
    let shipTypeLOS = '';
    let shipTypeMinDate = '';
    let shipTypeMaxDate = '';

    if (shippingOption === DeviceShippingOption.StorePickUp && selectedStore) {
      shipTypeToken = selectedStore.AvailabilityToken;
      shipTypeId = selectedStore.Id;
      shipTypeMinDate = selectedStore?.EstMinDate || '';
      shipTypeMaxDate = selectedStore?.EstMaxDate || '';
    } else if (shippingOption === DeviceShippingOption.ShipToHome && selectedService) {
      shipTypeToken = selectedService.AvailabilityToken;
      shipTypeLOS = selectedService.Name;
      shipTypeMinDate = selectedService?.EstMinArrivalDate || '';
      shipTypeMaxDate = selectedService?.EstMaxArrivalDate || '';
    }

    const line = {
      Active: active ? 'true' : 'false',
      Number: lineNumber,
      TransactionNumber: lineData?.phoneNumber?.transactionNumber ?? (lineData?.phoneNumber?.portNumber ?? ''), // applicable for port only for current phone number
      TransactionType: this.determineTransactionType(lineData, lineNumber, existingLinesCount), // new OR port or upgrade or existing
      PlanChoiceCode: lineData?.wirelessPlan?.planSelected ?? '', //WIRELESSPLAN
      PayPlan: lineData?.installmentPlan?.planSelected ?? '', // PAYMENT PLAN CHOICE CODE
      PayPlanCode: lineData?.installmentPlan?.Usoc ?? '', // PAYMENT PLAN Usoc
      InsurancePlan: lineData?.protectionPlan?.planSelected ?? '', // PROTECTION PLAN
      AIAWiFiExtender: "",
      DeviceDetails: {
        NewByodOnhand: lineData?.device?.deviceType?.toUpperCase() ?? '', // NEW, ONHAND = ONHAND, BYOD = BYOD
        Upc: lineData?.device?.selectedDevice?.Upc ?? '',
        DeviceSku: deviceSku,
        ByodIMEI: lineData?.device?.deviceIMEI ?? lineData?.device?.simIccid ?? '',
        ByodSIM: byodSim ?? '',
        DeviceIMEI: lineData?.device?.existingIMEI ?? '',
        ShipType: lineData?.device?.shippingOption ?? '',
        ShipTypeToken: shipTypeToken,
        ShipTypeStoreId: shipTypeId,
        ShipTypeLOS: shipTypeLOS,
        ShipTypeMaxDate: shipTypeMaxDate,
        ShipTypeMinDate: shipTypeMinDate,
        RequiredDownPayment: (+wirelessPdPlusLine?.CrsmDownPayment || lineData?.installmentPlan?.requiredDownPaymentAmount) ?? '',
        OptionalDownPayment: lineData?.installmentPlan?.optionalDownPaymentAmount ?? ''
      },
      RequiredDownPayment: (+wirelessPdPlusLine?.CrsmDownPayment || lineData?.installmentPlan?.requiredDownPaymentAmount) ?? '',
      OptionalDownPayment: lineData?.installmentPlan?.optionalDownPaymentAmount ?? '',
      Promotions: promotions
    } as any

    if (includePortInfo && lineData?.phoneNumber && lineData?.phoneNumber?.numberType === 'port') {
      line.PortInfo = {
        AccountNumber: lineData?.phoneNumber?.accountNumber ?? '',
        AccountName: lineData?.phoneNumber?.accountName ?? '',
        Pin: lineData?.phoneNumber?.pinNumber ?? '',
        Address: customer?.ServiceAddress.Address1,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
      }
    }

    return line;
  }

  determineTransactionType(lineData: SingleLineConfiguration, lineNumber: number, existingLinesCount: any) {
    if (lineData?.phoneNumber?.numberType) { // not upgrade
      return lineData.phoneNumber.numberType
    } else if (lineData?.upgradeDevice?.transactionType) { // if nextup upgrade
      return lineData.upgradeDevice.transactionType
    } else if (lineData?.device?.transactionType) { // new or port when new line
      return lineData.device.transactionType
    } else if (existingLinesCount >= lineNumber) {
      return 'existing'
    }
    return 'port'
  }

  getExistingLinesCount() {
    const viData = this.#orderStateServiceOld.getVIAccountDetails() as any;
    const lines = viData?.Lines?.Line
    return lines?.length || 0;
  }

  clearWirelessState(): void {
    this.wirelessSetupStateService.resetData();
    this.wirelessAdditionalOffersStateService.resetData();
    this.wirelessLineConfigurationStateService.resetData();
    this.sigfanStateService.resetData();
    this.businessVerificationStateService.resetData();
    this.wirelessFlowStateService.resetData();
    this.orderStateService.setWirelessProductDetails(null);
    this.orderStateService.setWirelessProductDetailsPlus(null);
    this.secureAccountStore.clear();
  }

  removeWirelessProduct(): Observable<any> {
    this.clearWirelessState();
    return this.orderApiCacheService.removeCacheDataByOrder(
      this.orderStateService.getOrder(),
      ['WIRELESS_PD', 'WIRELESS_PDPLUS']
    );
  }

}